import { createContext, PropsWithChildren, useContext } from 'react';

/**
 * Internal analytics context.
 *
 * Analytics events have two contexts:
 * - external context: the page URL, tracked automatically
 * - internal context: the physical page area where the event starts from
 *
 * Use this value only for the internal context, for example `/sidebar`,
 * `/table`, or `/filters`. Do not use it to describe pages or routes.
 */
export type AnalyticContextString = `/${string}`;
const AnalyticsReactContext = createContext<AnalyticContextString>('/');

interface AnalyticContextProps {
  /**
   * Physical page area to attach to descendant analytics events.
   */
  context: AnalyticContextString;
}

/**
 * Adds an internal analytics context for all tracked events fired by descendants.
 *
 * Important: use this only to distinguish physical page areas, not pages or routes.
 * You can also pass `context` manually to a track call when needed.
 * Nested providers concatenate their values, for example `/sidebar/form`.
 */
export const AnalyticsContext = ({ children, context }: PropsWithChildren<AnalyticContextProps>) => {
  const state = useAnalyticsContext();

  return (
    <AnalyticsReactContext.Provider value={state == '/' ? context : `${state}${context}`}>
      {children}
    </AnalyticsReactContext.Provider>
  );
};

/**
 * Returns the current internal analytics context.
 *
 * Prefer the automatically tracked external context when the event only needs
 * page-level information. Use this value only for page-area context.
 */
export const useAnalyticsContext = () => {
  return useContext(AnalyticsReactContext);
};
